import { QueryClient } from '@tanstack/react-query';

import { isApiError, problemCodeOf, ERROR_CODES } from '@/lib/apiProblem';

/**
 * The application query client, configured to the defaults recorded in ADR-0011:
 * `staleTime` 30 s, one retry for queries, no retry for mutations, no refetch on window focus for the wizard.
 *
 * A `4xx` is a deterministic answer (bad input, revoked session), so retrying it only delays the error the user
 * needs to read — that is why the retry predicate is status-aware instead of a plain count.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          const status = isApiError(error) ? error.problem.status : 0;

          if (status >= 400 && status < 500) {
            return false;
          }

          return failureCount < 1;
        },
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/** Codes that mean "sign in again": the session is gone or was rejected by the server. */
export const SESSION_LOST_CODES: readonly string[] = [
  ERROR_CODES.unauthenticated,
  ERROR_CODES.tokenExpired,
  ERROR_CODES.refreshTokenInvalid,
];

/** Determines whether a failure should send the user back to the sign-in screen. */
export function isSessionLost(error: unknown): boolean {
  const code = problemCodeOf(error);

  return code !== null && SESSION_LOST_CODES.includes(code);
}
